import React, { useEffect, useMemo, useState } from "react";
import LandscapeIcon from "@mui/icons-material/Landscape";
import Co2Icon from "@mui/icons-material/Co2";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import EuroIcon from "@mui/icons-material/Euro";
import FilterListIcon from "@mui/icons-material/FilterList";
import AnalysesFilters from "../analysesFilters/AnalysesFilters";
import { EMPTY_FILTERS } from "../../../conts/historyConsts";
import "./StatisticsStep.css";

/* ------------------------------ formatters ------------------------------ */
const nf = (digits = 0) =>
  new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const fmtInt = (v) => nf(0).format(Math.round(v ?? 0));

const fmtEUR = (v = 0) => {
  if (v >= 1e9) return { value: nf(2).format(v / 1e9), unit: "Md€" };
  if (v >= 1e6) return { value: nf(1).format(v / 1e6), unit: "M€" };
  if (v >= 1e3) return { value: nf(0).format(v / 1e3), unit: "k€" };
  return { value: nf(0).format(v), unit: "€" };
};

const fmtCO2 = (t = 0) => {
  if (t >= 1e5) return { value: nf(2).format(t / 1e6), unit: "MtCO₂e" };
  if (t >= 1e3) return { value: nf(1).format(t / 1e3), unit: "ktCO₂e" };
  return { value: nf(0).format(t), unit: "tCO₂e" };
};

const fmtArea = (v = 0) => ({ value: nf(0).format(v), unit: "ha" });
const fmtBuildings = (v = 0) => ({ value: fmtInt(v), unit: "bâti(s)" });

// accepts "2025-07-12" or "2025-07-12T00:00:00Z": keeps the date part only
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};

/* ------------------------------- metrics --------------------------------- */
// Metric colors are accents only (icon + dot). All text stays burgundy.
const METRICS = [
  {
    id: "value",
    key: "preserved_value_eur",
    label: "Valeur évitée",
    tab: "Valeur",
    color: "var(--color-burgundy)",
    tint: "var(--color-pink)",
    fmt: fmtEUR,
    shortUnit: null,
    Icon: EuroIcon,
  },
  {
    id: "area",
    key: "preserved_area_ha",
    label: "Surface sauvegardée",
    tab: "Surface",
    color: "var(--color-green-olive)",
    tint: "var(--color-green-pale)",
    fmt: fmtArea,
    shortUnit: "ha",
    Icon: LandscapeIcon,
  },
  {
    id: "co2",
    key: "co2_saved_t",
    label: "Carbone retenu",
    tab: "Carbone",
    color: "var(--color-purple)",
    tint: "var(--color-lavender)",
    fmt: fmtCO2,
    shortUnit: null,
    Icon: Co2Icon,
  },
  {
    id: "bld",
    key: "buildings_saved",
    label: "Infrastructures préservées",
    tab: "Bâti",
    color: "var(--color-burgundy)",
    tint: "var(--color-pink)",
    fmt: fmtBuildings,
    shortUnit: "",
    Icon: HomeWorkIcon,
  },
];

const colorVars = (m) => ({ "--m": m.color, "--m-tint": m.tint });

/* ------------------------------- count-up -------------------------------- */
const useCountUp = (target, duration = 900) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const reduce = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduce || !target) {
      setVal(target || 0);
      return;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setVal(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
};

/* ------------------------------ sub-parts -------------------------------- */
// value first, label below: values stay aligned even when a label wraps
const Kpi = ({ metric, total }) => {
  const animated = useCountUp(total);
  const f = metric.fmt(animated);
  const { Icon } = metric;
  return (
    <div className="stats-kpi" style={colorVars(metric)}>
      <div className="stats-kpi__row">
        <span className="stats-kpi__icon" aria-hidden="true">
          <Icon fontSize="inherit" />
        </span>
        <span className="stats-kpi__value">
          {f.value}
          <small>{f.unit}</small>
        </span>
      </div>
      <span className="stats-kpi__label">{metric.label}</span>
    </div>
  );
};

const Skeleton = () => (
  <div className="historic-step stats" aria-busy="true">
    <div className="stats-skel stats-skel--hero" />
    <div className="stats-skel-grid">
      {[0, 1, 2].map((i) => (
        <div key={i} className="stats-skel stats-skel--card" />
      ))}
    </div>
    <div className="stats-skel stats-skel--chart" />
  </div>
);
/* ------------------------------ stats filter ------------------------------ */
const NO_IDS = []; // stable reference (a new [] per render would loop the effects)

const isEmptyQuery = (q) =>
  !q || Object.values(q).every((v) => v === "" || v == null);

// `draft` lives in UserHistoricSidebar so it survives tab switches
const StatsFilter = ({
  statsIds,
  candidates,
  onFetchCandidates,
  onApplyIds,
  onResetIds,
  draft,
  onDraftChange,
}) => {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(statsIds);
  // "all" mode: every analysis of the current results is selected
  // (default when the global stats are displayed)
  const [allMode, setAllMode] = useState(statsIds.length === 0);
  // filters of the last search actually sent (draft may be edited without searching)
  const [searched, setSearched] = useState(draft);

  const isFiltered = statsIds.length > 0;
  const items = candidates?.data?.items ?? NO_IDS;
  const noSearchFilters = isEmptyQuery(searched);

  // keep the ticks in sync with the stats actually displayed
  useEffect(() => {
    setPicked(statsIds);
    setAllMode(statsIds.length === 0);
  }, [statsIds]);

  // in "all" mode, tick every analysis returned by the current search
  useEffect(() => {
    if (allMode) setPicked(items.map((it) => it.analysis_id));
  }, [allMode, items]);

  const toggleOpen = () => {
    if (!open) {
      // drop unapplied changes: start again from what is displayed
      setPicked(statsIds);
      setAllMode(statsIds.length === 0);
      setSearched(draft);
      // the shared `analyses` state may hold the list tab's results, so reload
      // with the filters entered here (kept, never cleared on open)
      onFetchCandidates({ ...draft, page: 1 });
    }
    setOpen((o) => !o);
  };

  const search = (values) => {
    setSearched(values);
    onFetchCandidates({ ...values, page: 1 });
  };

  const reset = () => {
    onDraftChange(EMPTY_FILTERS); // only "Réinitialiser" clears the inputs
    setSearched(EMPTY_FILTERS);
    onFetchCandidates({});
  };

  const togglePick = (id) => {
    setAllMode(false); // any manual change leaves "all" mode
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  };

  const selectAll = () => setAllMode(true);

  const clearAll = () => {
    setAllMode(false);
    setPicked([]);
  };

  // everything selected without any search filter = the real global stats
  const isGlobalSelection = allMode && noSearchFilters;

  const apply = () => {
    if (isGlobalSelection) onResetIds();
    else onApplyIds(picked);
    setOpen(false);
  };

  const allVisiblePicked =
    items.length > 0 && items.every((it) => picked.includes(it.analysis_id));

  const countLabel = isGlobalSelection
    ? "Toutes les analyses sont sélectionnées"
    : picked.length > 0
      ? `${picked.length} analyse${picked.length > 1 ? "s" : ""} sélectionnée${
          picked.length > 1 ? "s" : ""
        }`
      : "";

  return (
    <section className="stats-filter">
      <div className="stats-filter__bar">
        <button
          type="button"
          className={`stats-filter__toggle ${open ? "is-open" : ""}`}
          onClick={toggleOpen}
          aria-expanded={open}
        >
          <FilterListIcon fontSize="small" />
          Filtres
          {isFiltered && (
            <span className="stats-filter__badge">{statsIds.length}</span>
          )}
        </button>
        {isFiltered && (
          <button
            type="button"
            className="stats-filter__link"
            onClick={() => {
              setAllMode(true);
              onResetIds();
            }}
          >
            Statistiques globales
          </button>
        )}
      </div>

      {open && (
        <>
          <AnalysesFilters
            value={draft}
            onChange={onDraftChange}
            onSubmit={search}
            onReset={reset}
            submitLabel="Rechercher"
          />

          <div className="stats-filter__results">
            {candidates?.isLoading && <p className="stats-hint">Chargement…</p>}
            {candidates?.error && (
              <p className="stats__error">{candidates.error}</p>
            )}
            {!candidates?.isLoading &&
              !candidates?.error &&
              items.length === 0 && (
                <p className="stats-hint">Aucune analyse trouvée.</p>
              )}

            <ul className="stats-filter__list">
              {items.map((it) => {
                const checked = picked.includes(it.analysis_id);
                return (
                  <li key={it.analysis_id}>
                    <label
                      className={`stats-filter__row ${
                        checked ? "is-checked" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => togglePick(it.analysis_id)}
                      />
                      <span className="stats-filter__ref">{it.reference}</span>
                      <em>{fmtDate(it.fire_date)}</em>
                    </label>
                  </li>
                );
              })}
            </ul>

            <div className="stats-filter__footer">
              <span className="stats-filter__count" aria-live="polite">
                {countLabel}
              </span>
              <div className="stats-filter__footer-actions">
                <button
                  type="button"
                  className="stats-filter__clear"
                  disabled={items.length === 0 || (allMode && allVisiblePicked)}
                  onClick={selectAll}
                >
                  Tout sélectionner
                </button>
                <button
                  type="button"
                  className="stats-filter__clear"
                  disabled={picked.length === 0}
                  onClick={clearAll}
                >
                  Tout désélectionner
                </button>
                <button
                  type="button"
                  className="stats-filter__apply"
                  disabled={picked.length === 0 && !isGlobalSelection}
                  onClick={apply}
                >
                  Appliquer
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

/* -------------------------------- main ----------------------------------- */
const StatisticsStep = ({
  data,
  isLoading,
  error,
  onRetry,
  statsIds = NO_IDS,
  onApplyIds,
  onResetIds,
  candidates,
  onFetchCandidates,
  draft,
  onDraftChange,
}) => {
  const [selected, setSelected] = useState(null);
  const [opsFocus, setOpsFocus] = useState(null); // "valued" | "impact"

  const totals = data?.totals;
  const top = useMemo(() => data?.top_preserved ?? [], [data]);

  const metric = METRICS[0]; // the top 5 is ranked by preserved value only
  const maxValue = useMemo(
    () => Math.max(1, ...top.map((t) => t[metric.key] ?? 0)),
    [top, metric],
  );

  useEffect(() => setSelected(null), [data]);

  const heroValue = useCountUp(totals?.preserved_value_eur ?? 0);
  const opsTotal = useCountUp(totals?.operations ?? 0, 700);

  if (isLoading && !data) return <Skeleton />;

  // shown in every state so the user can never get stuck on a filtered view
  const filter = (
    <StatsFilter
      statsIds={statsIds}
      candidates={candidates}
      onFetchCandidates={onFetchCandidates}
      onApplyIds={onApplyIds}
      onResetIds={onResetIds}
      draft={draft}
      onDraftChange={onDraftChange}
    />
  );

  if (error && !data)
    return (
      <div className="historic-step stats stats--center">
        <p className="stats__error">{error}</p>
        <button type="button" className="stats__retry" onClick={onRetry}>
          Réessayer
        </button>
        {filter}
      </div>
    );

  if (!totals || totals.operations === 0)
    return (
      <div className="historic-step stats stats--center">
        <p className="stats__empty">
          Aucune analyse pour le moment. Lancez une première analyse pour voir
          vos statistiques ici.
        </p>
        {filter}
      </div>
    );

  const hero = fmtEUR(heroValue);
  const valued = totals.valued_operations ?? 0;
  const impactOnly = Math.max(totals.operations - valued, 0);
  const valuedPct = (valued / totals.operations) * 100;
  const impactPct = 100 - valuedPct;

  const sel = selected !== null ? top[selected] : null;
  const share = sel
    ? ((sel[metric.key] ?? 0) / (totals[metric.key] || 1)) * 100
    : 0;

  const opsText = {
    valued:
      "Analyses dont le calcul des coûts a abouti : la valeur évitée est chiffrée en euros.",
    impact:
      "Analyses limitées aux impacts environnementaux : surface, carbone et bâti, sans calcul des coûts.",
  };
  const plural = (n, word) => `${word}${n > 1 ? "s" : ""}`;
  return (
    <div className="historic-step stats">
      {/* filter ---------------------------------------------------------- */}
      {filter}

      {/* hero ------------------------------------------------------------ */}
      <section className="stats-hero">
        <p className="stats-hero__label">
          {statsIds.length > 0
            ? `Valeur évitée · ${statsIds.length} analyse${
                statsIds.length > 1 ? "s" : ""
              }`
            : "Valeur évitée cumulée"}
        </p>
        <p className="stats-hero__value">
          {hero.value}
          <small>{hero.unit}</small>
        </p>
      </section>

      {/* cumulative totals: one surface, three cells ---------------------- */}
      <section className="stats-kpis">
        {METRICS.filter((m) => m.id !== "value").map((m) => (
          <Kpi key={m.id} metric={m} total={totals[m.key] ?? 0} />
        ))}
      </section>

      {/* ranking ---------------------------------------------------------- */}
      <section className="stats-card" style={colorVars(metric)}>
        <h3 className="stats-title">
          Classement des valeurs préservées
          {valued > 0 && (
            <small className="stats-title__sub">
              {" "}
              · {fmtInt(top.length)} {plural(top.length, "analyse")}{" "}
              {plural(top.length, "chiffrée")}
            </small>
          )}
        </h3>

        {top.length === 0 ? (
          <p className="stats-hint">Aucune analyse à afficher.</p>
        ) : (
          <>
            <div className="stats-chart" role="list">
              {top.map((t, i) => {
                const raw = t[metric.key] ?? 0;
                const f = metric.fmt(raw);
                const isSel = i === selected;
                return (
                  <button
                    key={t.money_id}
                    type="button"
                    role="listitem"
                    title={t.reference}
                    aria-pressed={isSel}
                    aria-label={`${t.reference}, ${f.value} ${f.unit}`}
                    className={`stats-bar ${isSel ? "is-selected" : ""} ${
                      selected !== null && !isSel ? "is-dim" : ""
                    }`}
                    onClick={() => setSelected(isSel ? null : i)}
                  >
                    <span className="stats-bar__value">
                      {f.value} {f.unit}
                    </span>
                    <span className="stats-bar__track">
                      <span
                        className="stats-bar__fill"
                        style={{
                          height: `${Math.max((raw / maxValue) * 100, 3)}%`,
                          animationDelay: `${Math.min(i, 10) * 50}ms`,
                        }}
                      />
                    </span>
                    <span className="stats-bar__ref">{t.reference}</span>
                  </button>
                );
              })}
            </div>

            {!sel && (
              <p className="stats-hint">
                Touchez une barre pour voir le détail de l’analyse.
              </p>
            )}

            {sel && (
              <div
                className="stats-detail"
                key={sel.money_id}
                aria-live="polite"
              >
                <div className="stats-detail__head">
                  <div>
                    <strong>{sel.reference}</strong>
                    <span>{fmtDate(sel.fire_date)}</span>
                  </div>
                  <button
                    type="button"
                    className="stats-detail__close"
                    onClick={() => setSelected(null)}
                    aria-label="Fermer le détail"
                  >
                    ×
                  </button>
                </div>

                <div className="stats-detail__share">
                  <span style={{ width: `${Math.max(share, 2)}%` }} />
                </div>
                <p className="stats-detail__share-label">
                  {nf(1).format(share)} % de la valeur évitée cumulée
                </p>

                <dl className="stats-detail__grid">
                  {METRICS.map((m) => {
                    const f = m.fmt(sel[m.key] ?? 0);
                    return (
                      <div
                        key={m.id}
                        className="stats-detail__item"
                        style={colorVars(m)}
                      >
                        <dt>{m.tab}</dt>
                        <dd>
                          {f.value} <small>{f.unit}</small>
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              </div>
            )}
          </>
        )}
      </section>

      {/* operations breakdown ---------------------------------------------- */}
      <section className="stats-card stats-card--compact">
        <div className="stats-ops__head">
          <h3 className="stats-title">Opérations créées</h3>
          <p className="stats-ops__total">{fmtInt(opsTotal)}</p>
        </div>

        <div className="stats-split" role="group" aria-label="Répartition">
          {valued > 0 && (
            <button
              type="button"
              className={`stats-split__seg stats-split__seg--valued ${
                opsFocus === "valued" ? "is-focus" : ""
              }`}
              style={{ width: `${valuedPct}%` }}
              onClick={() =>
                setOpsFocus(opsFocus === "valued" ? null : "valued")
              }
              aria-pressed={opsFocus === "valued"}
              aria-label={`${valued} avec calcul des coûts`}
            />
          )}
          {impactOnly > 0 && (
            <button
              type="button"
              className={`stats-split__seg stats-split__seg--impact ${
                opsFocus === "impact" ? "is-focus" : ""
              }`}
              style={{ width: `${impactPct}%` }}
              onClick={() =>
                setOpsFocus(opsFocus === "impact" ? null : "impact")
              }
              aria-pressed={opsFocus === "impact"}
              aria-label={`${impactOnly} impacts seuls`}
            />
          )}
        </div>

        <ul className="stats-legend">
          {[
            {
              id: "valued",
              label: "Avec calcul des coûts",
              n: valued,
              pct: valuedPct,
            },
            {
              id: "impact",
              label: "Impacts environnementaux seuls",
              n: impactOnly,
              pct: impactPct,
            },
          ].map((o) => (
            <li key={o.id}>
              <button
                type="button"
                className={`stats-chip ${opsFocus === o.id ? "is-focus" : ""}`}
                onClick={() => setOpsFocus(opsFocus === o.id ? null : o.id)}
                aria-pressed={opsFocus === o.id}
              >
                <i className={`stats-legend__dot stats-legend__dot--${o.id}`} />
                <span className="stats-chip__label">{o.label}</span>
                <span className="stats-chip__count">
                  {fmtInt(o.n)} <em>{nf(0).format(o.pct)} %</em>
                </span>
              </button>
            </li>
          ))}
        </ul>

        {opsFocus && <p className="stats-ops__note">{opsText[opsFocus]}</p>}
      </section>
    </div>
  );
};

export default StatisticsStep;
